import { Group } from 'src/design/atoms/Group';
import { IconDownload } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useCallback, useMemo } from 'react';
import { workflowStatusColumn } from 'src/app/workflows/workflowStatusColumn';
import { useNavigate } from 'react-router-dom';
import { useJobs } from 'src/hooks/useJobs';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { ViewActionIcon } from 'src/design/atoms/ViewActionIcon';
import { TransmittalDto } from 'src/api/FiretrolApiClient';
import { usePreviewTransmittalPdf } from 'src/api/queries/transmittals/useTransmittalMutations';
import { useWorkflowPdfDownload } from 'src/app/workflows/useWorkflowPdfDownload';
import { useTableExports } from 'src/design/organisms/useTableExports';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';

interface ITransmittalsTableProps {
  transmittals: TransmittalDto[];
  loading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
}

export const TransmittalsTable: React.FC<ITransmittalsTableProps> = ({
  transmittals,
  loading = true,
  error,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const { selectedJob } = useJobs();
  const previewTransmittalPdf = usePreviewTransmittalPdf();
  const { download, downloadingId } = useWorkflowPdfDownload({
    fetchPdf: (jobId, id) => previewTransmittalPdf.mutateAsync({ jobId, id }),
    logLabel: 'Failed to download transmittal PDF',
  });

  const handleView = useCallback(
    (row: TransmittalDto) => {
      navigate(`/jobs/${row.jobId}/transmittals/${row.id}`);
    },
    [navigate],
  );

  const handleDownloadPdf = (row: TransmittalDto) => {
    if (!row.jobId) return;
    download({
      jobId: row.jobId,
      id: row.id,
      fileName: `${row.transmittalNumber ?? 'transmittal'}.pdf`,
    });
  };

  const columns = useMemo<MRT_ColumnDef<TransmittalDto>[]>(
    () => [
      {
        accessorKey: 'transmittalNumber',
        header: 'Transmittal Number',
        size: 200,
      },
      workflowStatusColumn<TransmittalDto>(),
      {
        id: 'submitting_to',
        header: 'Submitting To',
        accessorFn: (row) => row.submittedToContact?.name || '-',
      },
      {
        id: 'client_email',
        header: 'Client Email',
        accessorFn: (row) => row.submittedToContact?.email || '-',
      },
      {
        id: 'created_by',
        header: 'Created By',
        accessorFn: (row) => row.fromUser?.fullName || '',
      },
    ],
    [],
  );

  const tableState = useTableState({
    identifier: 'job-transmittals',
    columns: columns,
    defaultSorting: [{ id: 'transmittalNumber', desc: false }],
  });

  const tableExports = useTableExports<TransmittalDto>('transmittals', (data) =>
    data.map((item) => ({
      ['Job Name']: selectedJob?.name,
      ['Job Number']: selectedJob?.jobNumber,
      ['Transmittal Number']: item.transmittalNumber,
      ['Status']: item.status,
      ['Submitting To']: item.submittedToContact?.name || '-',
      ['Client Email']: item.submittedToContact?.email || '-',
      ['Created By']: item.fromUser?.fullName || '',
    })),
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 120 } },
      renderRowActions: ({ row }) => (
        <Group>
          <ViewActionIcon
            onClick={() => handleView(row.original)}
            label="View Transmittal"
          />
          <BasicActionIcon
            onClick={() => handleDownloadPdf(row.original)}
            label="Download PDF"
            loading={downloadingId === row.original.id}
          >
            <IconDownload size={20} />
          </BasicActionIcon>
        </Group>
      ),
      enableClickToCopy: false,
      columns: columns,
      data: transmittals,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
    },
    getRowId: (row) => String(row.id),
    totalCount: transmittals.length,
    isLoading: loading,
    isFetching: false,
    isError: !!error,
    error: error ?? undefined,
    onRefresh,
    ...tableExports,
  });

  return table;
};
